// src/OffersPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
// --- MODIFICATION: Import submitOfferFeedback and useSnackbar ---
import { getOffers, getUserReferralCode, setUserReferralCode, getUserReferralSpins, submitOfferFeedback } from './api'; // <-- Import submitOfferFeedback
import { useAuth } from './AuthContext';
// --- NEW: Import useSnackbar ---
import { useSnackbar } from 'notistack';
import { QRCodeCanvas } from 'qrcode.react';
import OfferFeedbackModal from './OfferFeedbackModal';
// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Paper from '@mui/material/Paper';
import ListItemIcon from '@mui/material/ListItemIcon';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack'; // <-- Import Stack
import TextField from '@mui/material/TextField'; // <-- Import TextField
import InputAdornment from '@mui/material/InputAdornment'; // <-- Import InputAdornment
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // <-- Import Copy Icon
import ShareIcon from '@mui/icons-material/Share'; // <-- Import Share Icon
// --- NEW: Import ReportProblemIcon ---
import { alpha } from '@mui/material/styles'; // <-- Import alpha for hover effect
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AdbIcon from '@mui/icons-material/Adb'; 


// --- Icons for Devices ---
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import DevicesIcon from '@mui/icons-material/Devices';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'; // <-- NEW: Import Desktop Icon
import ImageIcon from '@mui/icons-material/Image';

// --- Icons for Guide ---
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LaunchIcon from '@mui/icons-material/Launch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // <-- Icon for new user note
// --- NEW: Import FeedbackIcon ---
import FeedbackIcon from '@mui/icons-material/Feedback';

// --- Helper Function: Detect User Device ---
const detectDevice = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (/android/.test(ua)) {
        return 'android';
    }
    if (/iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
        return 'ios';
    }
    return 'desktop';
};

// Helper function to get device icons
const getDeviceIcons = (deviceString, size = "small") => { // Default size kept small here, overridden in list item
    const devices = (deviceString || '').toLowerCase();
    const icons = [];
    if (devices.includes('android')) {
        icons.push(<AndroidIcon key="android" fontSize={size} sx={{ verticalAlign: 'middle' }} />);
    }
    if (devices.includes('iphone') || devices.includes('ipad')) {
        icons.push(<AppleIcon key="ios" fontSize={size} sx={{ verticalAlign: 'middle' }} />);
    }
    if (devices.includes('desktop')) {
        icons.push(<DesktopWindowsIcon key="desktop" fontSize={size} sx={{ verticalAlign: 'middle' }} />);
    }
    if (icons.length === 0 && (devices.includes('all') || !deviceString)) {
        icons.push(<DevicesIcon key="all" fontSize={size} sx={{ verticalAlign: 'middle' }} />);
    }
    return <>{icons}</>;
};

// Helper Function: Check if offer device matches user device
const checkDeviceMatch = (offerDeviceString, userDeviceType) => {
    const offerDevices = (offerDeviceString || '').toLowerCase();
    if (offerDevices.includes('all') || !offerDeviceString) {
        return true;
    }
    if (userDeviceType === 'android' && offerDevices.includes('android')) {
        return true;
    }
    if (userDeviceType === 'ios' && (offerDevices.includes('iphone') || offerDevices.includes('ipad'))) {
        return true;
    }
    if (userDeviceType === 'desktop' && offerDevices.includes('desktop')) {
        return true;
    }
    return false;
};


function OffersPage() {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const [userDeviceType, setUserDeviceType] = useState('desktop');
    const [deviceFilter, setDeviceFilter] = useState('all');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    // --- NEW: Add enqueueSnackbar from useSnackbar ---
    const { enqueueSnackbar } = useSnackbar();

    // --- NEW: Referral State ---
    const [referralCode, setReferralCode] = useState(null);
    const [newReferralCodeInput, setNewReferralCodeInput] = useState('');
    const [referralLoading, setReferralLoading] = useState(false);
    const [referralError, setReferralError] = useState('');
    const [referralSuccessMessage, setReferralSuccessMessage] = useState('');
    const [referralSpinsEarned, setReferralSpinsEarned] = useState(0); // <-- State for referral spins
    // --- END NEW ---

    // --- NEW: Feedback Modal State ---
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [feedbackOffer, setFeedbackOffer] = useState(null); // Offer being reported
    // --- END NEW ---

    // --- NEW: Set Document Title ---
    useEffect(() => {
        document.title = 'SpinnerCS - Earn Spins';
    }, []);
    // --- END NEW ---

    useEffect(() => {
        const detectedType = detectDevice();
        setUserDeviceType(detectedType);
        // If user is on android or ios, default filter to their device type.
        // Otherwise (including desktop), default to 'all'.
        setDeviceFilter(
            (detectedType === 'android' || detectedType === 'ios') ? detectedType : 'all'
        );
    }, []);

    // --- MODIFIED: Fetch offers AND referral code ---
    useEffect(() => {
        if (user?.isAuthenticated) {
            setLoading(true); // For offers
            setReferralLoading(true); // For referral code + spins
            setError(null);
            setReferralError('');
            setReferralSpinsEarned(0); // Reset spins count

            getOffers()
                .then((data) => {
                    const sortedData = [...data].sort((a, b) => (b.epc ?? 0) - (a.epc ?? 0));
                    setOffers(sortedData);
                    setLoading(false);
                })
                .catch(err => {
                    setError('Failed to load tasks. Please try again later.');
                    console.error(err);
                    setLoading(false);
                });

            // Fetch referral code and spins in parallel
            Promise.all([getUserReferralCode(), getUserReferralSpins()])
                .then(([codeData, spinsData]) => {
                    // API now returns the code string directly, or "" if not set
                    setReferralCode(codeData || null); // Store the code string or null if empty/error
                    setReferralSpinsEarned(spinsData || 0); // Store the spins count, default to 0
                    setReferralLoading(false);
                })
                .catch((err) => {
                    // Handle potential errors from either API call
                    if (err.config?.url?.includes('referralcode')) {
                        setReferralError('Could not load referral code.');
                    } else if (err.config?.url?.includes('referralspins')) {
                        setReferralError('Could not load referral spin count.');
                    } else {
                        setReferralError('Could not load referral information.');
                    }
                    console.error(err);
                    setReferralLoading(false);
                });

        } else {
            setOffers([]);
            setReferralCode(null); // Clear referral code if logged out
            setReferralSpinsEarned(0); // Clear spins count if logged out
        }
    }, [user]);
    // --- END MODIFIED ---

    const handleFilterChange = (event, newFilter) => {
        if (newFilter !== null) {
            setDeviceFilter(newFilter);
        }
    };

    const handleOfferClick = (offer) => {
        setSelectedOffer(offer);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedOffer(null);
    };

    const filteredOffers = useMemo(() => {
        return offers.filter(offer => {
            const deviceString = (offer.device || '').toLowerCase();
            if (deviceFilter === 'all') return true;
            if (deviceFilter === 'android') return deviceString.includes('android');
            if (deviceFilter === 'ios') return deviceString.includes('iphone') || deviceString.includes('ipad');
            if (deviceFilter === 'desktop') return deviceString.includes('desktop');
            return true;
        });
    }, [offers, deviceFilter]);

    // --- NEW: Referral Code Handlers ---
    const handleCreateReferralCode = useCallback(async () => {
        if (!newReferralCodeInput.trim()) {
            setReferralError('Please enter a desired referral code.');
            return;
        }
        setReferralLoading(true);
        setReferralError('');
        setReferralSuccessMessage('');
        try {
            // Call the updated PUT function
            await setUserReferralCode(newReferralCodeInput.trim());
            // On success (204 No Content), update the local state with the input value
            setReferralCode(newReferralCodeInput.trim());
            setReferralSuccessMessage('Referral code created successfully!');
            setNewReferralCodeInput(''); // Clear input on success
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || 'Failed to create referral code.';
            // Handle specific errors based on documentation
            if (err.response?.status === 400 && errMsg.toLowerCase().includes('already has a referral code')) {
                setReferralError('You already have a referral code assigned.');
            } else if (err.response?.status === 409) { setReferralError('This referral code is already taken.'); } else { setReferralError(errMsg); }
            setReferralError(errMsg);
        } finally {
            setReferralLoading(false);
        }
    }, [newReferralCodeInput]);

    const handleCopyCode = useCallback(() => {
        if (!referralCode) return;
        navigator.clipboard.writeText(referralCode)
            .then(() => {
                setReferralSuccessMessage('Code copied to clipboard!');
                setTimeout(() => setReferralSuccessMessage(''), 2000); // Clear message after 2s
            })
            .catch(err => {
                setReferralError('Failed to copy code.');
                console.error('Copy failed:', err);
            });
    }, [referralCode]);

    const handleCopyLink = useCallback(() => {
        if (!referralCode) return;
        const link = `${window.location.origin}/referral/${referralCode}`; // Use origin for base URL
        navigator.clipboard.writeText(link)
            .then(() => {
                setReferralSuccessMessage('Link copied to clipboard!');
                setTimeout(() => setReferralSuccessMessage(''), 2000); // Clear message after 2s
            })
            .catch(err => { setReferralError('Failed to copy link.'); console.error('Copy failed:', err); });
    }, [referralCode]);
    // --- END NEW ---

    // --- NEW: Feedback Modal Handlers ---
    const handleOpenFeedbackModal = useCallback((offerToReport) => {
        setFeedbackOffer(offerToReport);
        setIsFeedbackModalOpen(true);
    }, []);

    const handleCloseFeedbackModal = useCallback(() => {
        setIsFeedbackModalOpen(false);
        setFeedbackOffer(null);
    }, []);

    const handleSubmitFeedback = useCallback(async (offerId, comment) => {
        try {
            await submitOfferFeedback({ offerId, comment });
            enqueueSnackbar(`Feedback for "${feedbackOffer?.name_short || 'offer'}" submitted. Thank you!`, { variant: 'success' });
            // No need to refresh offers list after submitting feedback
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || 'Failed to submit feedback.';
            enqueueSnackbar(errMsg, { variant: 'error' });
            throw err; // Re-throw to be caught by the modal for inline error display
        }
    }, [enqueueSnackbar, feedbackOffer]);
    // --- END NEW ---


    // --- Conditional Rendering ---
    if (!user?.isAuthenticated) {
        return <Alert severity="info">Please log in to see available tasks.</Alert>;
    }
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading tasks...</Typography>
            </Box>
        );
    }
    if (error) {
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }
    // --- End Conditional Rendering ---

    return (
        <Box sx={{ mt: 2 }}>

            {/* --- Available Tasks Title --- */}
            <Typography variant="h4" gutterBottom align="center" sx={{ mt: 2, mb: 3 }}> {/* Adjusted margins */}
                Available Tasks to Earn Spins
            </Typography>

            {/* --- How-To Guide --- */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, mb: 4, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom align="center" sx={{ mb: 1.5 }}>
                    How to Earn Spins
                </Typography>
                <List dense disablePadding>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <LooksOneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Browse & Select a Task:" secondary="Find a task that interests you." />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <LooksTwoIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Check Requirements:"
                            secondary={
                                <>
                                    Click the task for details. Pay attention to <SmartphoneIcon fontSize="inherit" sx={{ verticalAlign: 'bottom', mx: 0.2 }} /> device needs (Android/iOS) and the <AssignmentTurnedInIcon fontSize="inherit" sx={{ verticalAlign: 'bottom', mx: 0.2 }} /> specific instruction.
                                    {/* --- MODIFICATION: Add new user note --- */}
                                    <Typography variant="caption" component="div" sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: 'warning.main' }}>
                                        <InfoOutlinedIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                        Note: Tasks usually require you to be a **new user** (app/game not previously installed).
                                    </Typography>
                                    {/* --- END MODIFICATION --- */}
                                </>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <Looks3Icon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Complete the Task:"
                            secondary={
                                <>
                                    Use the <QrCodeScannerIcon fontSize="inherit" sx={{ verticalAlign: 'bottom', mx: 0.2 }} /> QR code or <LaunchIcon fontSize="inherit" sx={{ verticalAlign: 'bottom', mx: 0.2 }} /> link from the correct device to start and finish the required action.
                                </>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleOutlineIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Receive Spins:" secondary="Spins are usually credited shortly after the task provider confirms completion (this may take some time)." />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <WarningAmberIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Important Notice:"
                            secondary="Using adblockers, proxies, or VPNs may interfere with task completion tracking. Spins will NOT be awarded if such tools prevent proper tracking by our partners." />
                    </ListItem>
                    {/* --- NEW: Add step for reporting issues --- */}
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <FeedbackIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText primary="Report Issues:" secondary="If a task is faulty or doesn't credit, use the 'Report' button to let us know. Your feedback helps improve the platform." />
                    </ListItem>
                    {/* --- END NEW --- */}
                </List>
            </Paper>
            {/* --- End How-To Guide --- */}

            {/* --- Filter Controls --- */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <ToggleButtonGroup
                    value={deviceFilter}
                    exclusive
                    onChange={handleFilterChange}
                    aria-label="Device filter"
                    size="small"
                >
                    <ToggleButton value="all" aria-label="all devices">
                        <DevicesIcon sx={{ mr: 0.5 }} /> All
                    </ToggleButton>
                    <ToggleButton value="android" aria-label="android devices">
                        <AndroidIcon sx={{ mr: 0.5 }} /> Android
                    </ToggleButton>
                    <ToggleButton value="ios" aria-label="ios devices">
                        <AppleIcon sx={{ mr: 0.5 }} /> iPhone/iPad
                    </ToggleButton>
                    <ToggleButton value="desktop" aria-label="desktop devices">
                        <DesktopWindowsIcon sx={{ mr: 0.5 }} /> Desktop
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {/* --- End Filter Controls --- */}

            {/* --- Offers List --- */}
            {filteredOffers.length === 0 ? (
                <Typography align="center" sx={{ mt: 4 }}>
                    No tasks available matching your filter.
                </Typography>
            ) : (
                <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1 }}>
                    {filteredOffers.map((offer, index) => (
                        <React.Fragment key={offer.offerId}>
                            <ListItem
                                alignItems="center" // Changed from flex-start to center vertically
                                button
                                onClick={() => handleOfferClick(offer)}
                                sx={(theme) => ({ // Make sx a function of theme
                                    opacity: offer.completed ? 0.65 : 1,
                                    borderLeft: offer.completed ? '4px solid' : 'none',
                                    borderLeftColor: offer.completed ? 'success.main' : 'transparent',
                                    pl: offer.completed ? 1.5 : 2,
                                    py: 1.5, // Add some vertical padding
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out', // Base transition
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.action.hover, 0.7), // Slightly more pronounced hover
                                        boxShadow: theme.shadows[3], // Add a bit of shadow on hover
                                        transform: 'scale(1.01)', // Slight scale effect
                                    },
                                })}
                            // --- MODIFICATION: Removed secondaryAction here ---
                            // secondaryAction={
                            //     offer.completed && <Chip label="Completed" color="success" size="small" variant="outlined" sx={{ ml: 1 }} />
                            // }
                            >
                                <ListItemAvatar>
                                    <Avatar
                                        variant="rounded"
                                        sx={{ width: 60, height: 60, mr: 2, bgcolor: 'grey.800' }}
                                        alt={offer.name_short || 'Task image'}
                                        src={offer.picture}
                                    >
                                        {!offer.picture && <ImageIcon />}
                                    </Avatar>
                                </ListItemAvatar>

                                {/* --- MODIFICATION: Adjusted ListItemText --- */}
                                <ListItemText
                                    primary={
                                        <Tooltip title={offer.name || offer.name_short || 'Unnamed Task'} placement="top-start">
                                            <Typography variant="h6" component="span" noWrap sx={{ display: 'block', fontWeight: 500 }}> {/* Slightly less bold */}
                                                {offer.name_short || 'Unnamed Task'}
                                            </Typography>
                                        </Tooltip>
                                    }
                                    secondary={
                                        <React.Fragment>
                                            {offer.description && (
                                                <Typography
                                                    sx={{ display: 'block', mb: 0.5 }}
                                                    component="span"
                                                    variant="body2"
                                                    color="text.secondary"
                                                    noWrap
                                                >
                                                    {offer.description}
                                                </Typography>
                                            )}
                                            {offer.adcopy && (
                                                <Typography
                                                    sx={{ display: 'block', fontStyle: 'italic' }} // Removed mb: 1
                                                    component="span"
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                >
                                                    Task instruction: "{offer.adcopy}"
                                                </Typography>
                                            )}
                                            {/* Completed Chip moved here for better flow */}
                                            {offer.completed && <Chip label="Completed" color="success" size="small" variant="outlined" sx={{ mt: 0.5, height: 20 }} />}
                                        </React.Fragment>
                                    }
                                    sx={{ mr: 2 }} // Add margin to avoid overlap with the right side
                                />
                                {/* --- END MODIFICATION --- */}


                                {/* --- MODIFICATION: Box for Spins and Device Icons on the right --- */}
                                <Box sx={{ ml: 'auto', pl: 1 }}> {/* Push to the right */}
                                    <Stack direction="column" spacing={0.5} alignItems="center"> {/* Center items vertically */}
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {offer.spins > 0 && (
                                                <Chip
                                                    label={`Spins: ${offer.spins}`}
                                                    color="primary"
                                                    size="medium" // Make chip bigger
                                                    sx={{ fontWeight: 'bold', fontSize: '0.9rem' }} // Make text bolder/bigger
                                                />
                                            )}
                                            {/* --- NEW: Report Button --- */}
                                            <Tooltip title="Report an issue with this offer">
                                                <IconButton onClick={(e) => { e.stopPropagation(); handleOpenFeedbackModal(offer); }} size="small" color="warning">
                                                    <ReportProblemIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        {offer.spins === 0 && ( // Keep this below the row with report button if spins are 0
                                            <Tooltip title="Complete this task to be entered into our monthly jackpot raffle. No direct spins will be awarded for this task.">
                                                <Chip
                                                    label="Monthly Jackpot Entry"
                                                    color="secondary"
                                                    size="medium"
                                                    sx={{ fontWeight: 'bold', fontSize: '0.8rem', textAlign: 'center', whiteSpace: 'normal', height: 'auto', '& .MuiChip-label': { py: '4px', px: '8px' } }}
                                                />
                                            </Tooltip>
                                        )}
                                        {offer.device && ( // Only show device icons if offer.device is present
                                            <Tooltip title={`Devices: ${offer.device || 'Any'}`} placement="top">
                                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', color: 'text.secondary', mt: 0.5 }}>
                                                    {/* Increase icon size */}
                                                    {getDeviceIcons(offer.device, "medium")}
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Box>
                                {/* --- END MODIFICATION --- */}

                            </ListItem>
                            {index < filteredOffers.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                    ))}
                </List>
            )}
            {/* --- End Offers List --- */}

            {/* --- Referral Section (Moved to Bottom) --- */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mt: 5, mb: 4, bgcolor: 'background.paper' }}> {/* Added mt: 5 */}
                <Typography variant="h6" gutterBottom align="center" sx={{ mb: 2 }}>
                    Referral System
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3, maxWidth: '600px', mx: 'auto' }}>
                    Invite your friends to SpinnerCS! Share your unique referral code or link below.
                    You'll receive <Chip label="1 Bonus Spin" size="small" color="secondary" sx={{ mx: 0.5 }} />
                    for each friend who signs up using your referral and successfully completes at least one task.
                    Your friend will also receive <Chip label="1 Free Spin" size="small" color="success" sx={{ mx: 0.5 }} /> when they sign up using your link!
                    It's a great way to earn more spins together!
                </Typography>
                {!referralLoading && referralCode && (
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, fontWeight: 'medium' }}>
                        Total spins earned from referrals: <Chip label={referralSpinsEarned} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
                    </Typography>
                )}
                {!referralLoading && referralCode && referralSpinsEarned === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                        Keep sharing your code to earn more spins!
                    </Typography>
                )}
                {referralLoading && !referralCode && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 1 }} color="text.secondary">Loading referral info...</Typography>
                    </Box>
                )}
                {!referralLoading && referralCode && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>Your referral code:</Typography>
                        <Chip
                            icon={<ContentCopyIcon />}
                            label={referralCode}
                            onClick={handleCopyCode}
                            color="secondary"
                            sx={{ fontSize: '1.1rem', fontWeight: 'bold', p: 2.5, cursor: 'pointer', mb: 2 }}
                        />
                        <Typography variant="body1" sx={{ mb: 1 }}>Share your link:</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={`${window.location.origin}/referral/${referralCode}`}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Copy Link">
                                            <IconButton onClick={handleCopyLink} edge="end">
                                                <ShareIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: 2 }}
                        />
                        {referralSuccessMessage && <Alert severity="success" sx={{ justifyContent: 'center', mb: 1 }}>{referralSuccessMessage}</Alert>}
                        {referralError && !referralSuccessMessage && <Alert severity="error" sx={{ justifyContent: 'center', mb: 1 }}>{referralError}</Alert>}
                    </Box>
                )}
                {!referralLoading && !referralCode && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>Create your unique referral code to invite friends!</Typography>
                        <Stack direction="row" spacing={1} justifyContent="center">
                            <TextField
                                label="Desired Code"
                                variant="outlined"
                                size="small"
                                value={newReferralCodeInput}
                                onChange={(e) => setNewReferralCodeInput(e.target.value)}
                                disabled={referralLoading}
                            />
                            <Button variant="contained" onClick={handleCreateReferralCode} disabled={referralLoading || !newReferralCodeInput.trim()}>Create Code</Button>
                        </Stack>
                        {referralError && <Alert severity="error" sx={{ mt: 2, justifyContent: 'center' }}>{referralError}</Alert>}
                    </Box>
                )}
            </Paper>
            {/* --- END Referral Section --- */}

            {/* --- Offer Details Modal (No changes needed here based on request) --- */}
            <Dialog
                open={modalOpen}
                onClose={handleModalClose}
                aria-labelledby="offer-dialog-title"
                aria-describedby="offer-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                {selectedOffer && (
                    <>
                        <DialogTitle id="offer-dialog-title">
                            {selectedOffer.name || 'Task Details'}
                            <IconButton
                                aria-label="close"
                                onClick={handleModalClose}
                                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box sx={{ display: 'flex', mb: 2, gap: 2, alignItems: 'center' }}>
                                <Avatar
                                    variant="rounded"
                                    sx={{ width: 80, height: 80, bgcolor: 'grey.800' }}
                                    alt={selectedOffer.name_short || 'Task image'}
                                    src={selectedOffer.picture}
                                >
                                    {!selectedOffer.picture && <ImageIcon fontSize="large" />}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" component="div">
                                        {selectedOffer.name_short || 'Unnamed Task'}
                                    </Typography>
                                    {selectedOffer.spins > 0 && (
                                        <Chip label={`Spins: ${selectedOffer.spins}`} color="primary" size="medium" sx={{ mt: 1 }} />
                                    )}
                                    {selectedOffer.spins === 0 && (
                                        <Tooltip title="Complete this task to be entered into our monthly jackpot raffle. No direct spins will be awarded for this task.">
                                            <Chip
                                                label="Monthly Jackpot Entry"
                                                color="secondary"
                                                size="medium"
                                                sx={{ mt: 1, fontWeight: 'bold', fontSize: '0.8rem', textAlign: 'center', whiteSpace: 'normal', height: 'auto', '& .MuiChip-label': { py: '4px', px: '8px' } }} />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                            {selectedOffer.description && (
                                <DialogContentText id="offer-dialog-description" sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Description:</Typography>
                                    {selectedOffer.description}
                                </DialogContentText>
                            )}
                            {selectedOffer.adcopy && (
                                <DialogContentText sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Task instruction:</Typography>
                                    <Typography component="span" sx={{ fontStyle: 'italic' }}>"{selectedOffer.adcopy}"</Typography>
                                </DialogContentText>
                            )}
                            {selectedOffer.device && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Required Devices:</Typography>
                                    {getDeviceIcons(selectedOffer.device, "medium")}
                                    <Typography variant="body2" color="text.secondary">({selectedOffer.device || 'Any'})</Typography>
                                </Box>
                            )}
                            {selectedOffer.spins === 0 && (
                                <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ mt: 2, mb: 2 }}>
                                    This task awards <strong>0 spins</strong> directly.
                                    Completing it will enter you into our monthly jackpot raffle for a chance to win bigger prizes!
                                </Alert>
                            )}


                            {/* --- MODIFICATION: Add new user note also in modal --- */}
                            <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ mt: 2, mb: 2 }}>
                                Remember: Tasks usually require you to be a **new user** of the app/game. Completing the task may not grant spins if you have installed it before.
                            </Alert>
                            {/* --- END MODIFICATION --- */}


                            {selectedOffer.link && userDeviceType !== 'desktop' && !checkDeviceMatch(selectedOffer.device, userDeviceType) && (
                                <Alert
                                    severity="warning"
                                    icon={<WarningAmberIcon fontSize="inherit" />}
                                    sx={{ mt: 2, mb: 2 }}
                                >
                                    This task requires a different device ({selectedOffer.device}). Please scan the QR code using the correct device.
                                </Alert>
                            )}

                            {selectedOffer.link && (
                                <Box sx={{ mt: 3, textAlign: 'center' }}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Scan or Click to Start Task:
                                    </Typography>
                                {checkDeviceMatch(selectedOffer.device, userDeviceType) ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            href={selectedOffer.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            startIcon={<LaunchIcon />}
                                            sx={{ mt: 1, py: 1.5, px: 4, fontSize: '1.1rem' }}
                                        >
                                            Start Task
                                        </Button>
                                    ) : (
                                        <Box sx={{ p: 1, bgcolor: 'white', display: 'inline-block', borderRadius: 1, mt: 1 }}>
                                            <QRCodeCanvas
                                                value={selectedOffer.link}
                                                size={280}
                                                bgColor={"#ffffff"}
                                                fgColor={"#000000"}
                                                level={"L"}
                                                includeMargin={true}
                                            />
                                            {/* This specific message might need adjustment if QR is shown on desktop for mobile-only offers */}
                                            {userDeviceType === 'desktop' && !selectedOffer.device.toLowerCase().includes('desktop') && (
                                                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                                    This offer is for {selectedOffer.device}. Scan with the appropriate mobile device.
                                                </Typography>
                                            )}
                                            {userDeviceType !== 'desktop' && !checkDeviceMatch(selectedOffer.device, userDeviceType) && (
                                                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                                    Scan with your mobile device.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {selectedOffer.completed && (
                                <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
                                    You have already completed this task.
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleModalClose}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            {/* --- End Offer Details Modal --- */}

            {/* --- NEW: Feedback Modal --- */}
            {feedbackOffer && (
                <OfferFeedbackModal
                    open={isFeedbackModalOpen}
                    onClose={handleCloseFeedbackModal}
                    offer={feedbackOffer}
                    onSubmit={handleSubmitFeedback}
                />
            )}
        </Box>
    );
}

export default OffersPage;
